import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const StudentAdmitCard = () => {
  const { user } = useAuth();
  const [admitCard, setAdmitCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdmitCard = async () => {
      try {
        // Fetch student profile first
        const profileResponse = await api.get(`/students/user/${user?._id}`);
        
        // Fetch admit card data
        const admitCardResponse = await api.get(`/admit-cards/student/${profileResponse.data.data._id}`);
        
        setAdmitCard(admitCardResponse.data.data);
      } catch (error) {
        console.error('Error fetching admit card:', error);
        // Fallback to mock data if API fails
        const mockAdmitCard = {
          id: 'AC2023001',
          studentName: user?.name || 'John Doe',
          rollNumber: '12345',
          className: 'Grade 10',
          section: 'A',
          examName: 'Mid Term Examination 2023',
          examDate: 'December 15, 2023',
          examTime: '9:00 AM - 12:00 PM',
          venue: 'Room 101, Main Building',
          subjects: ['Mathematics', 'Science', 'English', 'Social Studies'],
          issuedDate: 'November 25, 2023',
          signature: 'Principal Signature',
          schoolName: 'EduManage School',
          schoolLogo: '/api/placeholder/100/100'
        };
        setAdmitCard(mockAdmitCard);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchAdmitCard();
    }
  }, [user]);

  const handleDownload = async () => {
    try {
      // In a real application, this would download the admit card as PDF
      const response = await api.get(`/admit-cards/download/${user?._id}`, {
        responseType: 'blob'  // Important for handling binary data
      });
      
      // Create a temporary link to download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admit-card-${user?._id}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading admit card:', error);
      alert('Failed to download admit card. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Admit Card</h1>
          <button
            onClick={handleDownload}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Download PDF
          </button>
        </div>

        {admitCard && (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-primary text-white p-6 text-center">
              <h2 className="text-2xl font-bold">{admitCard.schoolName}</h2>
              <p className="text-white opacity-90">Admit Card</p>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Student Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {admitCard.studentName}</p>
                    <p><span className="font-medium">Roll Number:</span> {admitCard.rollNumber}</p>
                    <p><span className="font-medium">Class:</span> {admitCard.className}</p>
                    <p><span className="font-medium">Section:</span> {admitCard.section}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Exam Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Exam:</span> {admitCard.examName}</p>
                    <p><span className="font-medium">Date:</span> {admitCard.examDate}</p>
                    <p><span className="font-medium">Time:</span> {admitCard.examTime}</p>
                    <p><span className="font-medium">Venue:</span> {admitCard.venue}</p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Subjects</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {admitCard.subjects.map((subject, index) => (
                    <div key={index} className="bg-gray-100 p-3 rounded text-center">
                      {subject}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">Issued Date: {admitCard.issuedDate}</p>
                    <p className="text-sm text-gray-600 mt-4">Signature: {admitCard.signature}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">For Office Use Only</p>
                    <div className="mt-4 p-4 border border-dashed border-gray-400 text-sm text-gray-600">
                      Verified by: _______________<br/>
                      Date: _______________
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-100 p-4 text-center text-sm text-gray-600">
              Please carry this admit card along with a valid photo ID to the examination hall
            </div>
          </div>
        )}

        <div className="mt-8 max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">Important Instructions:</h4>
            <ul className="list-disc list-inside text-yellow-700 space-y-1">
              <li>Carry this admit card and a valid photo ID to the examination hall</li>
              <li>Reach the examination center 30 minutes before the scheduled time</li>
              <li>Mobile phones and electronic devices are strictly prohibited</li>
              <li>Report to the invigilator immediately upon arrival</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAdmitCard;